import { Bell } from "common/Icons/Icons";
import { Bell as NewLookBell } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";
import { useContext, useRef, useState } from "react";
import NotificationsModal from "features/notifications/NotificationModal";
import { useIsOnboarding } from "utils/hooks/misc";
import { skipToken } from "@reduxjs/toolkit/query";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { IconProps } from "common/Icons/Icon";
import useSocketNotifications from "@shared/services/socket/useSocketNotifications";
import useNotificationsList from "@shared/features/notifications/useNotificationsList";
import { Counter } from "common/Home/Header/HeaderIconWithBadge";
import styled, { useTheme } from "styled-components";
import { sharedLib } from "@shared/services/shared.service";
import Badge from "antd/es/badge";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { NOTIFICATIONS_TRIGGER_ID, NOTIFICATIONS_COUNT_ID } from "testIds";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface NotificationsButtonProps {
  iconSize: IconProps["$size"];
}

const NotificationsButton = ({ iconSize }: NotificationsButtonProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const appTheme = useTheme();
  const { analytics } = useContext(AnalyticsContext);
  const isOnboarding = useIsOnboarding();
  const [notificationsModalOpen, setNotificationsModalOpen] = useState(false);
  const notificationsOpenTime = useRef<number | undefined>(undefined);
  useSocketNotifications();

  const { data: notifications } =
    sharedLib.sharedApi.notifications.useGetNotificationsQuery(
      isOnboarding ? skipToken : undefined
    );
  const {
    numUnreadNotifications,
    showNotificationsCounter,
    notificationCounterText,
    oneDigitCounter,
  } = useNotificationsList(notifications);

  const onOpenModal = () => {
    setNotificationsModalOpen(true);
    analytics?.showNotificationsClicked();
    notificationsOpenTime.current = new Date().getTime();
  };

  const onCloseModal = () => {
    setNotificationsModalOpen(false);
    if (notificationsOpenTime.current) {
      analytics?.closeNotificationsClicked({
        notificationsReviewTime:
          new Date().getTime() - notificationsOpenTime.current,
      });
      notificationsOpenTime.current = undefined;
    }
  };

  return (
    <NotificationsModal
      visible={notificationsModalOpen}
      onCloseModal={onCloseModal}
      onOpenModal={onOpenModal}
      numUnreadNotifications={numUnreadNotifications}
    >
      <NotificationBadge
        // undefined, not false: antd only hides the badge for a nullish (or
        // zero) count, and draws an empty red dot for `false`.
        count={
          showNotificationsCounter ? (
            <Counter
              data-testid={NOTIFICATIONS_COUNT_ID}
              $counterColor={appTheme.colors.red}
              $oneDigit={oneDigitCounter}
            >
              {notificationCounterText}
            </Counter>
          ) : undefined
        }
        offset={
          appTheme.isNewLook
            ? [oneDigitCounter ? -10 : -6, 12]
            : [oneDigitCounter ? -18 : -14, 12]
        }
        style={{ display: "flex" }}
      >
        <NotificationTrigger
          type="button"
          data-testid={NOTIFICATIONS_TRIGGER_ID}
          aria-label={t("labels.notifications")}
          aria-expanded={notificationsModalOpen}
          $active={notificationsModalOpen}
        >
          <LookIcon
            classic={<Bell $size={iconSize} style={{ marginRight: 8 }} />}
            newLook={<NewLookBell size={20} strokeWidth={1.75} />}
          />
        </NotificationTrigger>
      </NotificationBadge>
    </NotificationsModal>
  );
};

export default NotificationsButton;

const NotificationBadge = styled(Badge)`
  flex: none;
  margin-inline-end: 18px;
  @media (max-width: ${phoneMaxWidth}) {
    margin-inline-end: 0;
  }
`;
const NotificationTrigger = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  padding: 8px;
  border: 0;
  border-radius: 50%;
  background: ${(props) =>
    props.$active ? props.theme.colors.lightBlue : "transparent"};
  color: ${(props) =>
    props.$active
      ? props.theme.colors.primaryBlue
      : props.theme.colors.secondaryText};
  cursor: pointer;
  @media (max-width: ${phoneMaxWidth}) {
    svg {
      margin-right: 0 !important;
    }
    ${(props) =>
      props.theme.isNewLook &&
      `
      position: relative;
      &::before {
        content: "";
        position: absolute;
        inset: -4px;
      }
    `}
  }
  &:hover {
    color: ${(props) => props.theme.colors.primaryBlue};
    background: ${(props) => props.theme.colors.lightBlue};
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primaryBlue};
    outline-offset: 2px;
  }
`;
